import { useCallback, useEffect } from 'react';
import { useStore } from '@/store/store';
import { useDebounce } from '@/hooks/use-debounce';

/** Match `useDeviceOrientation` default so resize-driven layout work stays bounded during drag-resize. */
const RESIZE_DEBOUNCE_MS = 100;

export const useIsSearchOpen = () => {
    const isSearchOpen = useStore((s) => s.search.isSearchOpen);

    const syncNavWidthToDrawerLeft = useCallback(() => {
        const drawer = document.querySelector<HTMLElement>('.search-drawer');
        const nav = document.querySelector<HTMLElement>('.header-fixed nav');
        if (isSearchOpen && drawer && nav) {
            const drawerWidthPx = drawer.getBoundingClientRect().width;
            const drawerLeftFromViewport = window.innerWidth - drawerWidthPx;
            const navLeftFromViewport = nav.getBoundingClientRect().left;
            const widthPx = drawerLeftFromViewport - navLeftFromViewport;
            nav.style.width = `${Math.max(0, widthPx)}px`;
        } else if (nav) {
            nav.style.removeProperty('width');
        }
    }, [isSearchOpen]);

    const debouncedSyncNavWidthToDrawerLeft = useDebounce(syncNavWidthToDrawerLeft, RESIZE_DEBOUNCE_MS);

    useEffect(() => {
        if (isSearchOpen) {
            document.body.classList.add('no-scroll');
            document.body.classList.add('search-open');
        } else {
            document.body.classList.remove('no-scroll');
            document.body.classList.remove('search-open');
        }

        syncNavWidthToDrawerLeft();

        if (isSearchOpen) {
            requestAnimationFrame(() => {
                // sync again on the next effect run
                requestAnimationFrame(syncNavWidthToDrawerLeft);
            });
        }

        let detachResize: (() => void) | undefined;

        if (isSearchOpen) {
            window.addEventListener('resize', debouncedSyncNavWidthToDrawerLeft);
            detachResize = () => {
                window.removeEventListener('resize', debouncedSyncNavWidthToDrawerLeft);
            };
        }

        return () => {
            document.body.classList.remove('no-scroll');
            document.body.classList.remove('search-open');
            detachResize?.();
            document.querySelector<HTMLElement>('.header-fixed nav')?.style.removeProperty('width');
        };
    }, [isSearchOpen, syncNavWidthToDrawerLeft, debouncedSyncNavWidthToDrawerLeft]);
};
